import { useCallback, useEffect, useState } from 'react'
import { adDetectionApi } from '../../api'
import { ErrorState, errorMessage } from '../../components/ErrorState'
import type { BannerDetectionRow, BannerDetectionStats, BannerScanTarget, ScanTargetCategory } from '../../types'
import { useShell } from '../../shell/ShellContext'
import { currentQuery, navigate, useRoute, withQuery } from '../../shell/router'
import { Block, Kpi, PageHeader, SidePanel, StatusPill, ViewTabs } from '../../ui/ui'
import { useI18n, useT } from '../../i18n'
import { useAds } from './AdsContext'
import { TARGET_CATEGORIES, targetCategoryLabel, hostname, screenshotUrl } from './adsShared'
import { countryName } from './AdsModule'
import { ScanStatusBanner } from './AdsCampaigns'

type Tab = 'overview' | 'detections' | 'sites'

const WEEK = 7 * 86_400_000

export function AdsBanners() {
  useRoute()
  const { t, fmt } = useI18n()
  const { country } = useShell()
  const { refreshKey } = useAds()
  const query = currentQuery()
  const tab = (query.get('tab') as Tab) || 'overview'

  const [targets, setTargets] = useState<BannerScanTarget[] | null>(null)
  const [stats, setStats] = useState<BannerDetectionStats | null>(null)
  const [latest, setLatest] = useState<BannerDetectionRow[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)
  const [detection, setDetection] = useState<BannerDetectionRow | null>(null)

  const loadTargets = useCallback(() => {
    adDetectionApi.getBannerTargets(country || undefined).then(setTargets).catch((err) => setError(errorMessage(err)))
  }, [country])

  useEffect(() => {
    setError(null)
    loadTargets()
    adDetectionApi.getBannerDetectionStats(country || undefined).then(setStats).catch(() => setStats(null))
    adDetectionApi
      .getBannerDetections({ country: country || undefined, page: 1, limit: 8 })
      .then((r) => setLatest(r.data))
      .catch((err) => setError(errorMessage(err)))
  }, [country, refreshKey, loadTargets])

  const setTab = (t: Tab) => navigate(withQuery('ads/banners', { tab: t === 'overview' ? undefined : t }))

  const networks = new Set((targets ?? []).flatMap((t) => t.detectedNetworks))
  const stale = (targets ?? []).filter((t) => t.isActive && (!t.lastScannedAt || Date.now() - new Date(t.lastScannedAt).getTime() > WEEK)).length

  return (
    <>
      <PageHeader
        title={t('ads.bannersTitle')}
        description={t('ads.bannersDesc')}
        actions={
          <>
            <button type="button" className="btn btn-secondary" onClick={() => setAdding(true)}>{t('ads.addSite')}</button>
            <button type="button" className="btn btn-primary" onClick={() => navigate('ads/campaigns')}>{t('ads.scanSites')}</button>
          </>
        }
      />

      <ScanStatusBanner />
      {error && <ErrorState message={error} compact />}

      <div className="grid-kpi">
        <Kpi label={t('ads.kpiMonitoredSites')} value={targets ? targets.filter((x) => x.isActive).length : '—'} sub={targets ? t('ads.kpiTotalSites', { n: targets.length }) : undefined} onClick={() => setTab('sites')} />
        <Kpi label={t('ads.kpiBanners')} value={stats ? fmt.number(stats.total) : '—'} onClick={() => setTab('detections')} />
        <Kpi label={t('ads.kpiNetworks')} value={targets ? networks.size : '—'} sub={[...networks].slice(0, 3).join(', ') || undefined} />
        <Kpi label={t('ads.kpiStale')} value={targets ? stale : '—'} tone={stale > 0 ? 'warn' : undefined} onClick={() => setTab('sites')} />
      </div>

      <ViewTabs
        label={t('ads.display')}
        value={tab}
        onChange={setTab}
        options={[
          { value: 'overview', label: t('ads.tabOverview') },
          { value: 'detections', label: t('ads.tabAllBanners'), count: stats?.total },
          { value: 'sites', label: t('ads.tabSites'), count: targets?.length },
        ]}
      />

      {tab === 'overview' && (
        <>
          <div className="grid-2">
            <Block
              title={t('ads.latestBanners')}
              action={<button type="button" className="block-link" onClick={() => setTab('detections')}>{t('ads.allBannersLink')}</button>}
            >
              {latest === null ? (
                <div className="stat-skeleton" style={{ minHeight: 160 }} />
              ) : latest.length === 0 ? (
                <p className="empty-note">{t('ads.noBannerYet')}</p>
              ) : (
                <ul className="row-list">
                  {latest.map((d) => (
                    <li key={d.id}>
                      <button type="button" className="row-link" onClick={() => setDetection(d)}>
                        <span className="ads-size num">{d.width}×{d.height}</span>
                        <span className="row-main">
                          <strong>{d.advertiserName}</strong>
                          <span>{d.siteDomain} · {fmt.date(d.detectedAt)}</span>
                        </span>
                        <StatusPill>{d.adNetwork || t('ads.directSale')}</StatusPill>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </Block>
            <Block title={t('ads.topsTitle')}>
              {!stats || stats.total === 0 ? (
                <p className="empty-note">{t('ads.topsEmpty')}</p>
              ) : (
                <div className="ads-tops">
                  <div>
                    <span className="panel-section-title">{t('ads.colAdvertiser')}</span>
                    <ul className="ads-top-list">
                      {stats.byAdvertiser.slice(0, 5).map((a) => (
                        <li key={a.advertiserName}>
                          <button type="button" onClick={() => navigate(withQuery('ads/banners', { tab: 'detections', advertiser: a.advertiserName }))}>
                            <span>{a.advertiserName}</span>
                            <span className="num">{t('ads.topAdvertiserCount', { n: a.count, sites: a.sitesCount })}</span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <span className="panel-section-title">{t('ads.topSites')}</span>
                    <ul className="ads-top-list">
                      {stats.bySite.slice(0, 5).map((s) => (
                        <li key={s.siteDomain}>
                          <button type="button" onClick={() => navigate(withQuery('ads/banners', { tab: 'detections', site: s.siteDomain }))}>
                            <span>{s.siteDomain}</span>
                            <span className="num">{t('ads.topSiteCount', { n: s.count, uniq: s.uniqueAdvertisers })}</span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </Block>
          </div>
          <TargetsBlock targets={targets} limit={8} onChanged={loadTargets} onShowAll={() => setTab('sites')} />
        </>
      )}

      {tab === 'detections' && <DetectionsList country={country} onOpen={setDetection} refreshKey={refreshKey} />}
      {tab === 'sites' && <TargetsBlock targets={targets} onChanged={loadTargets} />}

      {detection && <DetectionPanel row={detection} onClose={() => setDetection(null)} />}
      {adding && (
        <AddTargetPanel
          defaultCountry={country}
          onClose={() => setAdding(false)}
          onSaved={() => {
            setAdding(false)
            loadTargets()
          }}
        />
      )}
    </>
  )
}

function DetectionsList({ country, onOpen, refreshKey }: { country: string; onOpen: (row: BannerDetectionRow) => void; refreshKey: number }) {
  const { t, fmt } = useI18n()
  const query = currentQuery()
  const advertiser = query.get('advertiser') ?? ''
  const site = query.get('site') ?? ''
  const page = Number(query.get('page')) || 1
  const [data, setData] = useState<{ data: BannerDetectionRow[]; total: number; totalPages: number } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [advertiserInput, setAdvertiserInput] = useState(advertiser)
  const [siteInput, setSiteInput] = useState(site)

  const go = (patch: Record<string, string | undefined>) =>
    navigate(withQuery('ads/banners', { tab: 'detections', advertiser: advertiser || undefined, site: site || undefined, page: undefined, ...patch }))

  useEffect(() => {
    setError(null)
    adDetectionApi
      .getBannerDetections({ country: country || undefined, advertiserName: advertiser || undefined, siteDomain: site || undefined, page, limit: 20 })
      .then((r) => setData({ data: r.data, total: r.total, totalPages: r.totalPages }))
      .catch((err) => setError(errorMessage(err)))
  }, [country, advertiser, site, page, refreshKey])

  return (
    <Block flush>
      <form
        className="ads-filters ads-filters-inset"
        onSubmit={(e) => {
          e.preventDefault()
          go({ advertiser: advertiserInput.trim() || undefined, site: siteInput.trim() || undefined })
        }}
      >
        <input type="search" value={advertiserInput} onChange={(e) => setAdvertiserInput(e.target.value)} placeholder={t('ads.filterAdvertiser')} aria-label={t('ads.filterAdvertiserAria')} />
        <input type="search" value={siteInput} onChange={(e) => setSiteInput(e.target.value)} placeholder={t('ads.filterSite')} aria-label={t('ads.filterSiteAria')} />
        <button type="submit" className="btn btn-secondary">{t('common.filter')}</button>
        {(advertiser || site) && (
          <button type="button" className="block-link" onClick={() => { setAdvertiserInput(''); setSiteInput(''); navigate(withQuery('ads/banners', { tab: 'detections' })) }}>
            {t('ads.clearFilters')}
          </button>
        )}
        {data && <span className="block-sub ads-filters-count">{t('ads.bannerCount', { n: data.total })}</span>}
      </form>
      {error ? (
        <ErrorState message={error} />
      ) : !data ? (
        <div className="stat-skeleton" style={{ minHeight: 200, margin: 16 }} />
      ) : data.data.length === 0 ? (
        <div className="table-empty">{t('ads.noBannerMatch')}</div>
      ) : (
        <div className="table-scroll">
          <table className="bet-table bet-table-clickable">
            <thead>
              <tr>
                <th scope="col">{t('ads.colAdvertiser')}</th>
                <th scope="col">{t('ads.filterSite')}</th>
                <th scope="col">{t('ads.colNetwork')}</th>
                <th scope="col">{t('ads.colFormat')}</th>
                <th scope="col">{t('ads.colAnalysis')}</th>
                <th scope="col">{t('common.date')}</th>
              </tr>
            </thead>
            <tbody>
              {data.data.map((d) => (
                <tr key={d.id} tabIndex={0} onClick={() => onOpen(d)} onKeyDown={(e) => { if (e.key === 'Enter') onOpen(d) }}>
                  <td>
                    <div className="bet-service-cell">
                      <strong>{d.advertiserName}</strong>
                      <span>{d.advertiserDomain || hostname(d.destinationUrl) || '—'}</span>
                    </div>
                  </td>
                  <td>
                    <div className="bet-service-cell">
                      <span className="bet-cell-strong">{d.siteDomain}</span>
                      {!country && <span>{countryName(t, d.country)}</span>}
                    </div>
                  </td>
                  <td>{d.adNetwork || t('ads.directSale')}</td>
                  <td className="num">{d.width}×{d.height} · {d.position}</td>
                  <td><VisionPill d={d} /></td>
                  <td className="num">{fmt.date(d.detectedAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {data && data.totalPages > 1 && (
        <div className="pagination">
          <button disabled={page <= 1} onClick={() => go({ page: String(page - 1) })}>← {t('common.previous')}</button>
          <span className="page-info">{t('common.page', { page, pages: data.totalPages })}</span>
          <button disabled={page >= data.totalPages} onClick={() => go({ page: String(page + 1) })}>{t('common.next')} →</button>
        </div>
      )}
    </Block>
  )
}

function VisionPill({ d }: { d: BannerDetectionRow }) {
  const t = useT()
  if (!d.visionAnalyzed) return <span className="bet-muted">{t('ads.visionNotAnalysed')}</span>
  if (d.visionRecommendation === 'BLOCK') return <StatusPill tone="crit">{t('ads.visionBlock')}</StatusPill>
  if (d.visionRecommendation === 'FLAG') return <StatusPill tone="warn">{t('ads.visionFlag')}</StatusPill>
  return <StatusPill tone="ok">{t('ads.visionClear')}</StatusPill>
}

function DetectionPanel({ row, onClose }: { row: BannerDetectionRow; onClose: () => void }) {
  const { t, fmt } = useI18n()
  return (
    <SidePanel
      wide
      title={row.advertiserName}
      subtitle={`${row.siteDomain} · ${fmt.dateTime(row.detectedAt)}`}
      onClose={onClose}
    >
      <>
          {row.screenshotFile && (
            <a className="ads-capture" href={screenshotUrl(row.screenshotFile)} target="_blank" rel="noopener noreferrer" title={t('ads.openCapture')}>
              <img src={screenshotUrl(row.screenshotFile)} alt={t('ads.bannerAlt', { name: row.advertiserName, site: row.siteDomain })} />
            </a>
          )}

          <section className="panel-section">
            <span className="panel-section-title">{t('ads.distribution')}</span>
            <dl className="field-grid">
              <div className="field"><dt>{t('ads.colNetwork')}</dt><dd>{row.adNetwork || t('ads.directSale')}</dd></div>
              <div className="field"><dt>{t('ads.colFormat')}</dt><dd className="num">{row.width}×{row.height} · {row.position}</dd></div>
              <div className="field"><dt>{t('ads.filterSite')}</dt><dd><a href={row.siteUrl} target="_blank" rel="noopener noreferrer">{row.siteTitle || row.siteDomain}</a></dd></div>
              <div className="field"><dt>{t('ads.destination')}</dt><dd>{row.destinationUrl ? <a href={row.destinationUrl} target="_blank" rel="noopener noreferrer">{hostname(row.destinationUrl)}</a> : '—'}</dd></div>
            </dl>
            {row.adText && <div className="ads-snippet"><span>{row.adText}</span></div>}
          </section>

          {row.visionAnalyzed && (
            <section className="panel-section">
              <div className="bet-panel-head">
                <span className="panel-section-title">{t('ads.imageAnalysis')}</span>
                <VisionPill d={row} />
              </div>
              <dl className="field-grid">
                {row.visionAdvertiser && <div className="field"><dt>{t('ads.visionAdvertiser')}</dt><dd>{row.visionAdvertiser}</dd></div>}
                {row.visionCategory && <div className="field"><dt>{t('common.category')}</dt><dd>{row.visionCategory}</dd></div>}
                {row.visionConfidence != null && <div className="field"><dt>{t('ads.visionConfidence')}</dt><dd className="num">{t('chart.percent', { v: row.visionConfidence })}</dd></div>}
                {row.visionPaymentMethods?.length ? <div className="field"><dt>{t('ads.visionPaymentMethods')}</dt><dd>{row.visionPaymentMethods.join(', ')}</dd></div> : null}
              </dl>
              {row.visionProhibited && (
                <p className="ads-prohibited">
                  {t('ads.visionProhibited', { detail: row.visionProhibitedDetails || t('common.yes').toLowerCase() })}
                </p>
              )}
              {row.visionSummary && <p className="field-note">{row.visionSummary}</p>}
            </section>
          )}

          <section className="panel-section">
            <span className="panel-section-title">{t('ads.technicalDetail')}</span>
            <dl className="field-grid">
              <div className="field"><dt>{t('ads.adSlot')}</dt><dd>{row.adSlotId || '—'}</dd></div>
              <div className="field"><dt>{t('ads.selector')}</dt><dd><code className="bet-mono">{row.selector || '—'}</code></dd></div>
              {row.destinationUrl && <div className="field" style={{ gridColumn: '1 / -1' }}><dt>{t('ads.fullUrl')}</dt><dd className="bet-url">{row.destinationUrl}</dd></div>}
            </dl>
          </section>
      </>
    </SidePanel>
  )
}

function TargetsBlock({
  targets,
  limit,
  onChanged,
  onShowAll,
}: {
  targets: BannerScanTarget[] | null
  limit?: number
  onChanged: () => void
  onShowAll?: () => void
}) {
  const { t, fmt } = useI18n()
  const [importing, setImporting] = useState(false)
  const [importResult, setImportResult] = useState<{ imported: number; skipped: number; errors: Array<{ row: number; url: string; reason: string }> } | null>(null)
  const [error, setError] = useState<string | null>(null)

  const sorted = [...(targets ?? [])].sort((a, b) => (b.lastScannedAt ?? '').localeCompare(a.lastScannedAt ?? ''))
  const shown = limit ? sorted.slice(0, limit) : sorted

  const toggle = async (target: BannerScanTarget) => {
    try {
      await adDetectionApi.updateBannerTarget(target.id, { isActive: !target.isActive })
      onChanged()
    } catch (err) {
      setError(errorMessage(err))
    }
  }

  const remove = async (target: BannerScanTarget) => {
    if (!window.confirm(t('ads.siteRemoveConfirm', { name: target.name }))) return
    try {
      await adDetectionApi.deleteBannerTarget(target.id)
      onChanged()
    } catch (err) {
      setError(errorMessage(err))
    }
  }

  const importCsv = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setImporting(true)
    setImportResult(null)
    setError(null)
    try {
      const lines = (await file.text()).split(/\r?\n/).filter((l) => l.trim())
      if (lines.length < 2) throw new Error(t('ads.emptyCsv'))
      const head = lines[0].toLowerCase()
      const sep = head.includes('\t') ? '\t' : head.includes(';') ? ';' : ','
      const headers = lines[0].split(sep).map((h) => h.trim().toLowerCase().replace(/^["']|["']$/g, ''))
      const col = (...names: string[]) => headers.findIndex((h) => names.includes(h))
      const urlIdx = col('url', 'site', 'lien')
      const countryIdx = col('country', 'pays', 'code_pays')
      const nameIdx = col('name', 'nom', 'site_name')
      const catIdx = col('category', 'categorie', 'catégorie', 'type')
      if (urlIdx === -1) throw new Error(t('ads.csvNoUrl'))
      if (countryIdx === -1) throw new Error(t('ads.csvNoCountry'))
      const rows = lines
        .slice(1)
        .map((line) => {
          const cols = line.split(sep).map((c) => c.trim().replace(/^["']|["']$/g, ''))
          return { url: cols[urlIdx] || '', country: cols[countryIdx] || '', name: nameIdx >= 0 ? cols[nameIdx] : undefined, category: catIdx >= 0 ? cols[catIdx] : undefined }
        })
        .filter((r) => r.url)
      const result = await adDetectionApi.importBannerTargets(rows)
      setImportResult(result)
      if (result.imported > 0) onChanged()
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setImporting(false)
      e.target.value = ''
    }
  }

  return (
    <Block
      flush
      title={t('ads.sitesBlock')}
      subtitle={limit ? t('ads.sitesBlockRecent') : t('ads.sitesBlockCsv')}
      action={
        <div className="bet-actions">
          {limit && onShowAll && targets && targets.length > limit && (
            <button type="button" className="block-link" onClick={onShowAll}>{t('ads.allSitesLink', { n: targets.length })}</button>
          )}
          {!limit && (
            <label className={`btn btn-secondary${importing ? ' is-busy' : ''}`}>
              {importing ? t('common.importing') : t('ads.importCsv')}
              <input type="file" accept=".csv,.tsv,.txt" hidden onChange={importCsv} disabled={importing} />
            </label>
          )}
        </div>
      }
    >
      {error && <div style={{ padding: '0 22px' }}><ErrorState message={error} compact /></div>}
      {importResult && (
        <div className="ads-import-result" role="status">
          <strong>{t('ads.importResultSites', { n: importResult.imported })}</strong>
          <span>{t('ads.importResultSkipped', { n: importResult.skipped })}</span>
          {importResult.errors.length > 0 && (
            <ul>
              {importResult.errors.slice(0, 20).map((e, i) => (
                <li key={i}>{t('ads.importRowError', { row: e.row, url: e.url ? ` (${e.url})` : '', reason: e.reason })}</li>
              ))}
            </ul>
          )}
          <button type="button" className="block-link" onClick={() => setImportResult(null)}>{t('common.hide')}</button>
        </div>
      )}
      {targets === null ? (
        <div className="stat-skeleton" style={{ minHeight: 160, margin: 16 }} />
      ) : shown.length === 0 ? (
        <div className="table-empty">{t('ads.noSiteMonitored')}</div>
      ) : (
        <div className="table-scroll">
          <table className="bet-table">
            <thead>
              <tr>
                <th scope="col">{t('ads.filterSite')}</th>
                <th scope="col">{t('common.country')}</th>
                <th scope="col">{t('ads.colNetworksFound')}</th>
                <th scope="col">{t('ads.colLastScan')}</th>
                {!limit && <th scope="col"><span className="sr-only">{t('common.actions')}</span></th>}
              </tr>
            </thead>
            <tbody>
              {shown.map((target) => (
                <tr key={target.id} className={target.isActive ? '' : 'bet-row-inactive'}>
                  <td>
                    <div className="bet-service-cell">
                      <strong>{target.name}</strong>
                      <span>
                        <a href={target.url} target="_blank" rel="noopener noreferrer">{hostname(target.url)}</a> · {targetCategoryLabel(t, target.category)}
                      </span>
                    </div>
                  </td>
                  <td>{countryName(t, target.country)}</td>
                  <td>{target.detectedNetworks.length ? target.detectedNetworks.join(', ') : <span className="bet-muted">—</span>}</td>
                  <td className="num">{target.lastScannedAt ? fmt.date(target.lastScannedAt) : <span className="bet-muted">{t('common.never')}</span>}</td>
                  {!limit && (
                    <td>
                      <div className="bet-actions">
                        <button type="button" className="btn btn-secondary" onClick={() => toggle(target)}>{target.isActive ? t('ads.siteSuspend') : t('ads.siteReactivate')}</button>
                        <button type="button" className="btn btn-danger" onClick={() => remove(target)}>{t('ads.siteRemove')}</button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Block>
  )
}

function AddTargetPanel({ defaultCountry, onClose, onSaved }: { defaultCountry: string; onClose: () => void; onSaved: () => void }) {
  const t = useT()
  const { countries } = useAds()
  const [url, setUrl] = useState('https://')
  const [name, setName] = useState('')
  const [country, setCountry] = useState(defaultCountry)
  const [category, setCategory] = useState<ScanTargetCategory>('NEWS')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const submit = async () => {
    setSaving(true)
    setError(null)
    try {
      await adDetectionApi.createBannerTarget({ url: url.trim(), name: name.trim(), country, category })
      onSaved()
    } catch (err) {
      setError(errorMessage(err))
      setSaving(false)
    }
  }

  const invalid = !/^https?:\/\/.+\..+/.test(url.trim()) || !name.trim() || !country

  return (
    <SidePanel
      title={t('ads.addSiteTitle')}
      subtitle={t('ads.addSiteSub')}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>{t('common.cancel')}</button>
          <button type="button" className="btn btn-primary" disabled={invalid || saving} onClick={submit}>
            {saving ? t('ads.adding') : t('ads.addSiteSubmit')}
          </button>
        </>
      }
    >
      <div className="form-stack">
        <label>
          {t('ads.address')}
          <input value={url} onChange={(e) => setUrl(e.target.value)} inputMode="url" placeholder="https://www.example.com" />
        </label>
        <label>
          {t('common.name')}
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder={t('ads.siteNamePlaceholder')} />
        </label>
        <label>
          {t('common.country')}
          <select value={country} onChange={(e) => setCountry(e.target.value)}>
            <option value="">{t('ads.pick')}</option>
            {countries.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
          </select>
        </label>
        <label>
          {t('common.category')}
          <select value={category} onChange={(e) => setCategory(e.target.value as ScanTargetCategory)}>
            {TARGET_CATEGORIES.map((k) => <option key={k} value={k}>{targetCategoryLabel(t, k)}</option>)}
          </select>
        </label>
        {error && <ErrorState message={error} compact />}
      </div>
    </SidePanel>
  )
}
